import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllFishChips,
  getSingleFishChips,
  createFishChips,
  updateFishChips,
  deleteFishChips,
} from "@/api/new-api/staff-management/menu-manage/fish-chips";
import { websiteOrderingAvailableItemsToggle } from "@/api/new-api/staff-management/menu-manage/drink";

// Thunks

export const fetchAllFishChips = createAsyncThunk(
  "fishChips/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllFishChips(restaurant_id);
    if (res.status === "success") return res.data.fish_chips;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleFishChips = createAsyncThunk(
  "fishChips/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleFishChips(id);
    if (res.status === "success") return res.data.fish_chips;
    return rejectWithValue(res.message);
  }
);

export const createNewFishChips = createAsyncThunk(
  "fishChips/create",
  async (data, { rejectWithValue }) => {
    const res = await createFishChips(data);
    if (res.status === "success") return res.data.fish_chips;
    return rejectWithValue(res.message);
  }
);

export const updateExistingFishChips = createAsyncThunk(
  "fishChips/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateFishChips(id, data);
    if (res.status === "success") return res.data.fish_chips;
    return rejectWithValue(res.message);
  }
);

export const deleteFishChipsById = createAsyncThunk(
  "fishChips/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteFishChips(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const updateFishChipsToggle = createAsyncThunk("fishChipsToggle/update", async ({ id, type, toggleAvailabilityType }, { rejectWithValue }) => {
    const res = await websiteOrderingAvailableItemsToggle(id, type, toggleAvailabilityType);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
});

// Initial State

const initialState = {
  fishChips: [],
  singleFishChip: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const fishChipsSlice = createSlice({
  name: "fishChips",
  initialState,
  reducers: {
    clearFishChipsMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllFishChips.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllFishChips.fulfilled, (state, action) => {
        state.getLoading = false;
        state.fishChips = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllFishChips.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch One
      .addCase(fetchSingleFishChips.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleFishChips.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleFishChip = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleFishChips.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewFishChips.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createNewFishChips.fulfilled, (state, action) => {
        state.createLoading = false;
        state.fishChips.push(action.payload);
        state.success = "Fish & Chips item created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewFishChips.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingFishChips.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateExistingFishChips.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.fishChips = state.fishChips.map((item) =>
          item.id === action.payload.id ? action.payload : item
        );
        state.success = "Fish & Chips item updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingFishChips.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteFishChipsById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteFishChipsById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.fishChips = state.fishChips.filter(
          (item) => item.id !== action.payload
        );
        state.success = "Fish & Chips item deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteFishChipsById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle website/QRcode availability
      .addCase(updateFishChipsToggle.pending, (state) => {
          state.updateLoading = true;
          state.error = null;
          state.requestStatus = false;
      })
      .addCase(updateFishChipsToggle.fulfilled, (state, action) => {
        state.updateLoading = false;

        const { id, toggleAvailabilityType } = action.meta.arg;

        state.fishChips = state.fishChips.map((drink) => {
          if (drink.id !== id) return drink;

          if (toggleAvailabilityType === "qr_code") {
            return {
              ...drink,
              is_qr_code_order_available: !drink.is_qr_code_order_available,
            };
          }

          return {
            ...drink,
            is_website_order_available: !drink.is_website_order_available,
          };
        });

        state.success = "Updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateFishChipsToggle.rejected, (state, action) => {
          state.updateLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
      })
  },
});

export const { clearFishChipsMessages } = fishChipsSlice.actions;
export default fishChipsSlice.reducer;
